import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";

import { App } from "./App";
import { ApiError } from "./lib/api";
import { AuthProvider } from "./lib/auth";
import { ToastProvider } from "./lib/toast";
import "./styles.css";

// A 401 from any request means the session is gone: expired, or evicted by a
// password change on another device. The shell flips to the login form at
// once instead of leaving a panel full of "nie udało się wczytać".
const onUnauthenticated = (error: unknown) => {
  if (error instanceof ApiError && error.isUnauthenticated) queryClient.setQueryData(["session"], null);
};

// Only an outage deserves a second try; a 401/403/404 will answer the same way.
const final = (error: unknown) => error instanceof ApiError && (error.isUnauthenticated || error.status === 403 || error.status === 404);

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onUnauthenticated }),
  mutationCache: new MutationCache({ onError: onUnauthenticated }),
  defaultOptions: {
    queries: {
      // Door and bar staff act on live data; a stale pass list could show a
      // revoked card as valid.
      staleTime: 10_000,
      retry: (count, error) => !final(error) && count < 1,
      refetchOnWindowFocus: true,
    },
    mutations: {
      // A save made without signal fails at once with "brak połączenia"
      // instead of waiting, spinner turning, for a network that may come
      // back an hour later and replay it behind the manager's back.
      networkMode: "always",
    },
  },
});

const root = document.getElementById("root");
if (!root) throw new Error("missing #root element");

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <App />
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
